import {
  AmplitudeContext,
  AmplitudeEventConstants,
} from '@homebot/shared-util-quartermaster'
import CountUp from 'react-countup'
import { Waypoint } from 'react-waypoint'
import usePrev from '../../../../hooks/usePrev'
import { IconEquals, IconMinus } from '../../Icon'
import { defineMessages, useIntl } from 'react-intl'
import { useState, useContext, useEffect } from 'react'
import YourLoanSentence from '../../../homeowners/YourLoanSentence'
import { AdditionalInfo } from '../../../shared/AdditionalInfo'
import { useAmplitudeDebounceSetters } from '../../../../hooks/tracking'
import { DisclaimersButton, Format, Text, Lock } from '../../../'
import { Button, Icon } from '@homebot/shared-ui-swashbuckle'

import styles from './NetWorthSection.module.scss'
import { ConfirmLoanDetailsInfoWrapper } from '../../InterestSection/PaidSoFar/ConfirmLoanDetailsInfoWrapper'

export const MSG = defineMessages({
  header: {
    id: 'Homeowner.estimatedValue.netWorthHeadline',
    defaultMessage: 'That means the net worth of the home to you is',
  },
  explanation: {
    id: 'Homeowner.estimatedValue.equityExplanation',
    defaultMessage:
      'If you sold your home today, this is approximately how much you would put in your pocket.',
  },
  toggle: {
    id: 'Homeowner.estimatedValue.equityInfoToggleButton',
    defaultMessage: 'View Calculation',
  },
  toggleOff: {
    id: 'Homeowner.estimatedValue.equityInfoToggleOffButton',
    defaultMessage: 'Hide Calculation',
  },
  additionalInfoHeader: {
    id: 'Homeowner.estimatedValue.netWorthExplainHeadline',
    defaultMessage: 'Your net worth ("equity") is made up of two things.',
  },
  homeVal: {
    id: 'General.homeValue',
    defaultMessage: 'Home value',
  },
  loanBalance: {
    id: 'Homeowner.estimatedValue.loanBalance',
    defaultMessage: 'Loan balance',
  },
  yourNetWorth: {
    id: 'Homeowner.estimatedValue.yourNetWorth',
    defaultMessage: 'Your net worth',
  },
  whereInfoComesFrom: {
    id: 'Homeowner.estimatedValue.whereDoesInformationQ',
    defaultMessage: 'Where does this information come from?',
  },
  forNetWorth: {
    id: 'Homeowner.estimatedValue.equityInfoToggleButtonContext',
    defaultMessage: 'for Net Worth',
  },
})

interface NetWorthSectionProps {
  homeValue: number
  estimatedValue: number
  hasLoans: boolean
  outstandingPrincipal: number
  withCurrentHomeData: boolean
}

export const NetWorthSection = ({
  homeValue,
  estimatedValue,
  hasLoans,
  outstandingPrincipal,
  withCurrentHomeData,
}: NetWorthSectionProps) => {
  const intl = useIntl()
  const { ampli } = useContext(AmplitudeContext)
  const prevEstimatedValue = usePrev(estimatedValue)
  const [countAnimating, setCountAnimating] = useState(false)
  const [showInfo, setShowInfo] = useState(false)

  const handleNetWorthToggle = () => {
    ampli?.netWorthExplored()
    setShowInfo(i => !i)
  }

  const [trackModuleViewed, clearDebounceForTrackModuleViewed] =
    useAmplitudeDebounceSetters(() =>
      ampli?.moduleViewed({ module_name: AmplitudeEventConstants.NET_WORTH })
    )

  useEffect(() => {
    return () => {
      clearDebounceForTrackModuleViewed()
    }
  }, [clearDebounceForTrackModuleViewed])

  return (
    <>
      {withCurrentHomeData && <ConfirmLoanDetailsInfoWrapper />}
      <Text
        element='h3'
        size='l'
        weight='light'
        className={styles.heading}
        variant='neutralCool'
        variantCount='900'
      >
        {intl.formatMessage(MSG.header)}
        <span className='screenReaderText'>${estimatedValue.toFixed(0)}</span>
      </Text>
      <Text
        size='xxl'
        variant='primary'
        variantCount='600'
        weight='light'
        className={styles.avm}
        element='div'
        data-animation-qa={countAnimating ? 'started' : 'done'}
      >
        <Lock enabled={!withCurrentHomeData}>
          <CountUp
            start={prevEstimatedValue ?? 0}
            end={estimatedValue}
            separator=','
            prefix='$'
            onStart={() => setCountAnimating(true)}
            onEnd={() => setCountAnimating(false)}
          />
        </Lock>
      </Text>
      {Boolean(estimatedValue > 1000) && (
        <Text
          variant='neutralCool'
          variantCount='700'
          size='s'
          className={styles.soldText}
        >
          {intl.formatMessage(MSG.explanation)}
        </Text>
      )}

      {withCurrentHomeData && (
        <>
          <Waypoint onEnter={trackModuleViewed} bottomOffset='50%'>
            <div />
          </Waypoint>
          <Button
            onClick={handleNetWorthToggle}
            aria-expanded={showInfo ? true : false}
            colorScheme='btn-secondary'
            leftIcon={<Icon name='calculator' height='1rem' width='1rem' />}
          >
            {showInfo
              ? intl.formatMessage(MSG.toggleOff)
              : intl.formatMessage(MSG.toggle)}
            <span className='screenReaderText'>
              {' '}
              {intl.formatMessage(MSG.forNetWorth)}
            </span>
          </Button>

          <AdditionalInfo
            visible={showInfo}
            headingText={intl.formatMessage(MSG.additionalInfoHeader)}
          >
            <div className={styles.equityEquation}>
              <div className={styles.equationGroup}>
                <div className={styles.equationGroupLabel}>
                  <Text
                    size='s'
                    weight='light'
                    variant='neutralCool'
                    variantCount='900'
                  >
                    {intl.formatMessage(MSG.homeVal)}
                  </Text>
                </div>
                <div className={styles.valueBoxEmphasized}>
                  <Format.Currency value={homeValue} />
                </div>
              </div>
              <IconMinus className={styles.iconMinus} />
              <div className={styles.equationGroup}>
                <div className={styles.equationGroupLabel}>
                  <Text
                    size='s'
                    weight='light'
                    variant='neutralCool'
                    variantCount='900'
                  >
                    {intl.formatMessage(MSG.loanBalance)}
                  </Text>
                </div>
                <div className={styles.valueBox}>
                  <Format.Currency value={Math.round(outstandingPrincipal)} />
                </div>
              </div>
              <IconEquals className={styles.iconEquals} />
              <div className={styles.equationGroup}>
                <div className={styles.equationGroupLabel}>
                  <Text
                    size='s'
                    weight='light'
                    variant='neutralCool'
                    variantCount='900'
                  >
                    {intl.formatMessage(MSG.yourNetWorth)}
                  </Text>
                </div>
                <div className={styles.valueBoxEmphasized}>
                  <Format.Currency value={Math.round(estimatedValue)} />
                </div>
              </div>
            </div>
            {hasLoans && (
              <Text
                element='h3'
                weight='light'
                variant='neutralCool'
                variantCount='900'
                paragraph
              >
                {intl.formatMessage(MSG.whereInfoComesFrom)}
              </Text>
            )}
            <Text size='s' variant='neutralCool' variantCount='900'>
              <YourLoanSentence long />
            </Text>
            <DisclaimersButton />
          </AdditionalInfo>
        </>
      )}
    </>
  )
}
